import React, { useState, useEffect, useMemo } from "react";
import { Page } from 'components/common';
import { NotificationManager } from "react-notifications";
import styles from "assets/scss/fixed_assets.module.css";
import { Trans } from 'react-i18next';
import { Link } from "react-router-dom";
import { AddNewButton } from "components/common/Tables";
import { useOrganisationContext, useUserContext } from "hooks";
import { ApiClient, } from "services";
import { formatDateName } from "services/util";
import ImportModal from 'components/ImportModal';
import { Label, Nav, Button } from "reactstrap";
import { exportToCSV } from "services/exportToCSV";
import { GenericTable } from "pages/PayrollHrNew/AttendanceReport/GenericTable";
import { SelectionCell, SelectionHeader } from "components/react-table";
import { commonNotes } from "pages/UserList/modelNames";
import { createPortal } from "react-dom";


const FixedAssetList = ({ match, t }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('DRAFT');
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [assetSettings, setAssetSettings] = useState({});
    const [assetTypes, setAssetTypes] = useState({});
    const [isOpen, setIsOpen] = useState(false);
    const { user } = useUserContext();
    const [refetch, setRefetch] = useState(false);
    const [pending, setPending] = useState(false);
    const [export_data, setExportData] = useState([])

    const client = ApiClient();

    const { selectedOrganisation } = useOrganisationContext();
    const connectedOrg = selectedOrganisation ? selectedOrganisation?.id : 0;
    const userPermission = user?.groups[0]?.name === "Superadmin";

    const columns = useMemo(
        () =>
            assetTypes ? [
                {
                    id: "selection",
                    Cell: SelectionCell,
                    Header: SelectionHeader,
                    disableSortBy: true,
                },
                {
                    Header: t("Asset Name"),
                    accessor: "assetName",
                    disableSortBy: true,
                    isVisible: false,
                },
                {
                    Header: t("Asset Number"),
                    accessor: "assetNumber",
                    Cell: ({ value, row: { original } }) => (
                        <Link to={`/fixed-assets/detail/${original.assetId}`}>{value}</Link>
                    ),

                },
                {
                    Header: t("Asset Type"),
                    accessor: "assetTypeId",
                    Cell: ({ value, row: { original } }) => (
                        <span><Trans>{assetTypes?.[value]?.assetTypeName || 'Unknown'}</Trans></span>
                    ),
                },
                {
                    Header: t("Purchase Date"),
                    accessor: "purchaseDate",
                    disableSortBy: false,
                },
                {
                    Header: t("Purchase Price"),
                    accessor: "purchasePrice",
                    disableSortBy: false,
                },
                {
                    Header: t("Disposal Date"),
                    accessor: "disposalDate",
                    disableSortBy: false,
                },
                {
                    Header: t("Disposal Price"),
                    accessor: "disposalPrice",
                    disableSortBy: false,
                },
            ] : [],
        [assetTypes]
    );



    function handleButtonClick() {
        window.open('https://fixedassets.xero.com/?#depreciate', '_blank');
    };

    useEffect(() => {
        fetchAssetNumbers();
        fetchAssetTypes();
    }, []);

    const fetchAssetTypes = async () => {
        try {
            const response = await client.get(`/asset-types/${connectedOrg}/assettypes`);
            setAssetTypes(Object.fromEntries(response.data.map(obj => [obj.assetTypeId, obj])));
        } catch (error) {
            NotificationManager.error(t("Error fetching asset types"));
            console.error(error);
        }
    };

    const _fetchAssets = async (pageIndex, pageSize) => {
        const response = await client.get(`/fixed-assets/${connectedOrg}/assets`, {
            params: {
                query: {
                    Status: activeTab,
                    page: parseInt(pageIndex) + 1,
                    pageSize: pageSize,
                    filterBy: searchTerm
                }
            }
        });
        setExportData(response.data)
        return response.data
    }

    const handleRegisterAssets = async (status) => {
        try {
            const response = await client.put(`/fixed-assets/${connectedOrg}/assets/register_draft_assets`, {
                selectedAssets: selectedAssets.map(asset => asset.original)
            }, {
                params: {
                    Status: status
                }
            });
            setRefetch(!refetch);
            NotificationManager.success(t("Assets registered successfully"));
        } catch (error) {
            NotificationManager.error(t('Check missing details (Deprecation date, Asset type...) and try again'));
        }
    };

    const fetchAssetNumbers = async () => {
        const response = await client.get(`/get-asset-number/${connectedOrg}/settings`);
        setAssetSettings(response.data);
    };

    const handleAssetImport = async (selectedFile) => {
        setPending(true)
        const formData = new FormData();
        formData.append('file', selectedFile)
        client.post(`/import_bulk_assets/${connectedOrg}/assets`, formData)
            .then(({ data }) => {
                NotificationManager.success(t("File imported successfully"));
                toggle();
                setPending(false);
                setRefetch(!refetch);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    NotificationManager.error(t(error.response.data.error));
                } else {
                    NotificationManager.error(t("Error importing assets"));
                }
                setPending(false);
                setRefetch(!refetch);
            });
    };
    const toggle = () => {
        setIsOpen(!isOpen);
    };

    const exportData = export_data?.items?.map((i) => ({
        Name: i.assetName,
        Number: i.assetNumber,
        Status: i.assetStatus,
        Type: assetTypes?.[i.assetTypeId]?.assetTypeName,
        PurchaseDate: i.purchaseDate,
        PurchasePrice: i.purchasePrice,
        BookValue: i.accountingBookValue ? i.accountingBookValue : null,
    }))

    return (
        <Page className='inv-tabs nw-list'>
            {pending &&
                createPortal(
                    <div className="content">
                        <div className="loader"></div>
                    </div>, document.querySelector('#loader-holder')
                )}
            <Label className="mb-4"><h4><Trans>Fixed Assets</Trans></h4></Label>
            <div className={` ${styles.asset_buttons}`}>
                <ul>
                    {userPermission && (
                        <>
                            <li>
                                <AddNewButton resource="Asset" match={match} />
                            </li>
                            <li>
                                {/* <AddNewButton resource="Run Depreciation" fullURL="/fixed-assets/run-depreciation"/> */}
                                <Button color="primary" onClick={() => handleButtonClick()}><Trans>Run Depreciation</Trans></Button>

                            </li>
                            <li>
                                <Button color="primary" onClick={() => toggle()}><Trans>Import</Trans></Button>
                            </li>
                            <li>
                                <Button onClick={() => exportToCSV({ data: exportData, filename: "Assets" })} color="primary">
                                    <i className="fas fa-file-excel mr-2"></i><Trans>Export</Trans>
                                </Button>
                            </li>
                            <li>
                                <Button color="primary"><Link style={{ backgroundColor: "#72b566", color: 'white' }} to={`/asset-types-list`}><Trans>Settings</Trans></Link></Button>
                            </li>
                            <li>
                                <Trans>Last Depreciation:</Trans> {assetSettings.lastDepreciationDate ? formatDateName(assetSettings.lastDepreciationDate) : "None"}
                            </li>
                        </>
                    )}
                </ul>
            </div>
            <div className={` ${styles.navigation}`}>
                <ul>
                    {userPermission && (
                        <>
                            <Nav tabs className="nw-tabs2 inv-tabs">
                                <div onClick={() => setActiveTab('DRAFT')} className={activeTab == 'DRAFT' ? `active-tabNum` : `` + 'clickable'}>
                                    <Trans>Draft</Trans>
                                </div>
                                <div onClick={() => setActiveTab('REGISTERED')} className={activeTab == 'REGISTERED' ? `active-tabNum` : `` + 'clickable'}>
                                    <Trans>Registered</Trans>
                                </div>
                                <div onClick={() => setActiveTab('DISPOSED')} className={activeTab == 'DISPOSED' ? `active-tabNum` : `` + 'clickable'}>
                                    <Trans>Sold & Disposed</Trans>
                                </div>
                            </Nav>
                        </>
                    )}
                </ul>
            </div>
            <div className="form-group">
                <div className="my-2">
                    <input className="form-control w-100"
                        type="search"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setTimeout(() => setRefetch(!refetch), 500) }}
                        placeholder={t("Search...")}
                    />
                </div>
                <div className="pt-1">
                    {activeTab === "DRAFT" && (
                        <Button type="submit" color="primary" onClick={() => handleRegisterAssets("DRAFT")} disabled={selectedAssets?.length === 0}><Trans>Register</Trans></Button>
                    )}
                </div>
                <GenericTable columns={columns} fetch={_fetchAssets} setSelectedRows={setSelectedAssets} queryParams={['assets', activeTab]} reload={activeTab} refetch={refetch} key={activeTab} />
                {/*refetch is a trigger to refetch, it can be passed as anything*/}
            </div>
            <ImportModal
                isOpen={isOpen}
                toggle={toggle}
                title="FixedAssets"
                handleUpload={handleAssetImport}
                importantNotes={commonNotes}
            >
            </ImportModal>
        </Page>
    );
};

export default FixedAssetList;
